import { LoadingOutlined } from '@ant-design/icons';
import Table from 'common/table/table';
import { Icons, RowIconAction, ThemeHeader } from 'common/table/table-helper';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Div, Icon, SubTitle } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import { blockConfig, viewConfig } from '../../common/gantt/config';
import { defaultView } from '../../common/gantt/config/view';
// eslint-disable-next-line max-len
import BlockEditSidepanelContainer from '../../common/gantt/containers/block-edit-sidepanel-container';
import { fetchBlockData, resetSocketState } from '../../common/gantt/containers/gantt-actions';
import {
  blockTypes,
  publishSteps,
} from '../../utils/app-configs';
import { getDuration, isObjectNullOrEmpty } from '../../utils/helpers';
import usePermissionCheck from '../../utils/hooks/usePermissionCheck';
import useMultiStatusPoll from '../../utils/useMultiStatusPoll';

const SuccessIcon = styled(Icon)`
  color: ${p => p.theme.colors.publishSteps.finish}
`;

const ProcessIcon = styled(LoadingOutlined)`
  color: ${p => p.theme.colors.publishSteps.process}
`;

const Wrapper = styled(Div)`
  box-shadow: rgba(0, 0, 0, 0.27) 0px;  
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const Status = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const StatusText = styled.p`
  margin-left: 10px;
`;

const Name = styled.span`
  margin-left: 5px;
`;

const DraftIcon = styled(Icon)`
  color: white !important;
  background-color: ${p => p.backgroundColor};
  padding: 2px 4px;
  border-radius: 5px;
  height: 16px;
  line-height: 13px;
`;

const SyncIcon = styled.div`
  background-color: ${p => p.backgroundColor};
  padding: 0px 4px;
  border-radius: 5px;
  height: 20px !important;
  display: flex;
  align-items: center;
  img {
    margin-top: 0px !important;
  }
`;

const RescheduleIcon = styled(Icon)`
  color: #64747c !important;
`;

const StyledDiv = styled(Div)`
    overflow: hidden;
`;

const CircleIcon = styled.div`
  height: 17px;
  width: 17px;
  border: 1px solid ${p => p.color};
  ${(p) => {
    if (p.isTentative) {
      return css`
        background: repeating-linear-gradient(
          120deg,
          #fff,
          #fff 3px,
          ${p.color} 3px,
          ${p.color} 6px
        );
      `;
    }
    return css`
      background: ${p.color};
    `;
  }}
  border-radius: 50%;
`;

const Planning = ({
  plannings = [],
  intl,
  openPlanningAndView,
  history,
  getLinkedPlanningsRequest,
  cbPlanningChange,
  entityId,
  match,
  useSockets,
}) => {
  const dispatch = useDispatch();
  const {
    block,
    deletedPlanningFromSockets = [],
    updatedPlanningFromSockets = [],
  } = useSelector(state => state.gantt);

  const planningItems = plannings?.map(p => ({
    ...p.planning,
    id: p.planning.planningId,
    operationInfo: {
      isDraft: p.planning.isDraft,
      isAssignedPlanner: p.planning.isAssignedPlanner,
      assignedPlannerName: p.planning.assignedPlannerName,
      isTentative: p.planning.isTentative,
      id: p.planning.planningId,
      ...p.operationInfo,
    },
  }));
  const [prevBlockData, setPrevBlockData] = useState({});
  const [wasPolling, setWasPolling] = useState([]);
  const [multiStatusInfo] = useMultiStatusPoll(
    planningItems,
    (id) => {
      if (cbPlanningChange) {
        dispatch(cbPlanningChange());
      } else {
        dispatch(getLinkedPlanningsRequest({
          boardId: viewConfig.boardIdTypes.service,
          entityId,
        }));
      }
      if (id && !useSockets) {
        dispatch(fetchBlockData({
          id,
          boardId: viewConfig.boardIdTypes.service,
          isSidePanel: true,
        }));
      }
    },
    useSockets,
  );
  const [checkPermission] = usePermissionCheck();

  useDeepCompareEffect(() => {
    if (!match?.params?.planningId && planningItems?.length) {
      const deleted = deletedPlanningFromSockets.filter(p => planningItems.find(v => v.id === p));
      if (deleted?.length) {
        dispatch(resetSocketState(deleted));
      }
      const updated = updatedPlanningFromSockets.filter(p => planningItems.find(v => v.id === p));
      if (updated?.length) {
        dispatch(resetSocketState(updated));
      }
    }
  }, [deletedPlanningFromSockets, updatedPlanningFromSockets]);

  useDeepCompareEffect(() => {
    // update the order whenever you have a update in a block
    if (match.params?.orderIds && !isObjectNullOrEmpty(block)) {
      if (!isEqual(block, prevBlockData) && !isObjectNullOrEmpty(prevBlockData)) {
        cbPlanningChange?.();
      }
    }
    setPrevBlockData(block);
  }, [block]);

  const handleRedirectPB = (planningId) => {
    openPlanningAndView({
      id: planningId,
      type: blockTypes.planning,
      viewId: defaultView.id,
      history,
      boardId: viewConfig.boardIdTypes.service,
      intl,
    });
  };

  const renderHeader = () => <ThemeHeader>{intl.formatMessage({
    id: 'order.LINKED_PLANNING_ITEMS',
  })}</ThemeHeader>;

  const columns = [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      permission: 'planboardread',
      renderFn: row => <Icons>
        <RowIconAction
          title={intl.formatMessage({ id: 'planningBoard.blocks.OPEN_ORDER_PLANNING' })}
          tooltipKey='pen'
          iconKey='pen'
          action={() => history.push(`edit/field_service/${row.planningId}`)}
          disabled={false}
        />
        <RowIconAction
          title={intl.formatMessage({ id: 'planningBoard.blocks.OPEN_PB_PLANNING' })}
          tooltipKey='arrow-up-right-from-square'
          iconKey='arrow-up-right-from-square'
          action={() => handleRedirectPB(row.planningId)}
          disabled={false}
        />
      </Icons>,
    },
    {
      id: 'laneDescription',
      displayName: intl.formatMessage({ id: 'order.fieldEngineer.FIELD_SERVICE_ENGINEER' }),
      fieldName: 'laneDescription',
      tooltip: row => row.laneDescription,
      width: '275px',
    },
    {
      id: 'startDate',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.START_DATE' }),
      fieldName: 'startDate',
      width: '150px',
      renderFn: value => moment(value.startDate).format('DD/MM/YYYY'),
    },
    {
      id: 'startLabour',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.START_LABOUR' }),
      fieldName: 'startLabour',
      width: '150px',
      renderFn: value => moment(value.startLabour, 'HH:mm').format('HH:mm'),
    },
    {
      id: 'durationLabour',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_LABOUR' }),
      fieldName: 'durationLabour',
      width: '150px',
      renderFn: value => getDuration(value.durationLabour),
    },
    {
      id: 'durationTravel',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_TRAVEL' }),
      fieldName: 'durationTravel',
      width: '150px',
      renderFn: value => getDuration(value.durationTravel),
    },
    {
      id: 'isTentative',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.PLANNING_STATUS' }),
      fieldName: 'isTentative',
      width: '200px',
      renderFn: (value) => {
        if (!value?.isTentative) {
          return <Status>
            <CircleIcon color={blockConfig.colors.default} />
            <StatusText>
              <FormattedMessage id='planningBoard.blocks.PLANNING_CONFIRMED' />
            </StatusText>
          </Status>;
        }
        return <Status>
          <CircleIcon color={blockConfig.colors.default} isTentative={true} />
          <StatusText>
            <FormattedMessage id='planningBoard.blocks.PLANNING_TENTATIVE' />
          </StatusText>
        </Status>;
      },
    },
    {
      id: 'operationInfo',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.PUBLISHING_STATUS' }),
      fieldName: 'operationInfo',
      width: '350px',
      renderFn: (row) => {
        const itemOperationInfo =
          multiStatusInfo?.find(i => i.id === row.operationInfo?.id)?.operationInfo ||
          row.operationInfo;
        if (
          wasPolling?.find(id => id === row.operationInfo.id) &&
          itemOperationInfo?.status === publishSteps.done.status &&
          !row.operationInfo.isDraft
        ) {
          setWasPolling(ids => ids?.filter(i => i !== row.operationInfo.id));
        }
        if (
          itemOperationInfo?.errors?.length ||
          itemOperationInfo?.isStale ||
          itemOperationInfo?.isTimedOut
        ) {
          return (
            <Status>
              {row.operationInfo.isAssignedPlanner ?
                <DraftIcon
                  type={blockConfig.isDraft.icon}
                  size='sm'
                  backgroundColor={blockConfig.isDraft.errorColor}
                /> :
                <SyncIcon backgroundColor={blockConfig.lock.color}>
                  <Icon
                    type={blockConfig.lock.draftIcon}
                    customIcon={true}
                    color={blockConfig.lock.color}
                  />
                </SyncIcon>
              }

              <StatusText>
                <FormattedMessage id='planningBoard.blocks.TO_BE_CORRECTED' />
                {' ('}
                <FormattedMessage id='planningBoard.blocks.BY' />
                <Name>
                  {
                    row.operationInfo.isAssignedPlanner ?
                      intl.formatMessage({ id: 'planningBoard.blocks.YOU' })?.toLowerCase() :
                      row.operationInfo.assignedPlannerName
                  })
                </Name>
              </StatusText>
            </Status>
          );
        } else if (
          (
            itemOperationInfo?.status >= publishSteps.unknown.status &&
            itemOperationInfo?.status < publishSteps.done.status
          ) || wasPolling?.find(id => id === row.operationInfo.id)
        ) {
          if (!wasPolling?.find(id => id === row.operationInfo.id)) {
            setWasPolling(ids => [...ids, row.operationInfo.id]);
          }
          return (
            <Status>
              <ProcessIcon />
              <StatusText>
                <FormattedMessage id='planningBoard.blocks.PUBLISHING' />
                {' ('}
                <FormattedMessage id='planningBoard.blocks.BY' />
                <Name>
                  {
                    row.operationInfo.isAssignedPlanner ?
                      intl.formatMessage({ id: 'planningBoard.blocks.YOU' })?.toLowerCase() :
                      row.operationInfo.assignedPlannerName
                  })
                </Name>
              </StatusText>
            </Status>
          );
        } else if (
          row.operationInfo?.isDraft &&
          !wasPolling?.find(id => id === row.operationInfo.id)
        ) {
          return (
            <Status>
              {row.operationInfo.isAssignedPlanner ?
                <DraftIcon
                  type={blockConfig.isDraft.icon}
                  size='sm'
                  backgroundColor={blockConfig.isDraft.color}
                /> :
                <SyncIcon backgroundColor={blockConfig.lock.color}>
                  <Icon
                    type={blockConfig.lock.draftIcon}
                    customIcon={true}
                    color={blockConfig.lock.color}
                  />
                </SyncIcon>
              }
              <StatusText>
                <FormattedMessage id='planningBoard.blocks.NOT_PUBLISHED' />
                {' ('}
                <FormattedMessage id='planningBoard.blocks.BY' />
                <Name>
                  {
                    row.operationInfo.isAssignedPlanner ?
                      intl.formatMessage({ id: 'planningBoard.blocks.YOU' }) :
                      row.operationInfo.assignedPlannerName
                  })
                </Name>
              </StatusText>
            </Status>
          );
        }
        return (
          <Status>
            <SuccessIcon type='check-circle' />
            <StatusText>
              <FormattedMessage id='planningBoard.blocks.PUBLISHED' />
            </StatusText>
          </Status>
        );
      },
    },
    {
      id: 'additionalInfo',
      displayName: intl.formatMessage({ id: 'planningBoard.blocks.ADDITIONAL_INFO' }),
      fieldName: 'additionalInfo',
      width: '250px',
      renderFn: (row) => {
        if (row.reschedulePlanning?.length) {
          return row.reschedulePlanning?.map(v => <Status key={v.id}>
            <RescheduleIcon type={v.icon} light={true} />
            <StatusText>
              {v.description}
            </StatusText>
          </Status>);
        }
        return null;
      },
    },
  ];

  return (
    <>
      <StyledDiv marginTop={45}>
        <SubTitle>
          {intl.formatMessage({ id: 'order.PLANNING' })}
        </SubTitle>
        <Wrapper>
          <Table
            labelInColumn={true}
            columns={columns?.filter(c => !c.permission || checkPermission(c.permission))}
            data={planningItems}
            fullWidth={true}
            renderCustomHeader={renderHeader}
          />
        </Wrapper>
      </StyledDiv>
      <Routes>
        <Route
          path={'edit/:type/:planningId'}
          element={<BlockEditSidepanelContainer
            blockCurrentStatusses={multiStatusInfo}
          />}
        />
      </Routes>
    </>
  );
};

Planning.propTypes = {
  match: PropTypes.object,
  plannings: PropTypes.array,
  intl: PropTypes.object,
  openPlanningAndView: PropTypes.func,
  history: PropTypes.object,
  getLinkedPlanningsRequest: PropTypes.func,
  cbPlanningChange: PropTypes.func,
  entityId: PropTypes.string,
  useSockets: PropTypes.bool,
};


export default withRouter(injectIntl(Planning));
